import React from 'react';
import { Dir } from '..';
import { useModal } from '../../../components/v2/Modal/useModal';
import FluentForm from '../../../components/v2/Form/FluentForm';
import { refetchActiveQueries } from '../../../utils/QueryClientProvider';
import { patchUpdateDir } from '../service/api';
import Section from '../../../components/v2/Section/Section';
import DirectoryTitleField from './DirectoryTitle';
import { useDirQuery } from '../hooks';
import { useAppFeatures } from '../../ui/selectors/app';
import { useAppSettings } from '../../ui/hooks';
import Loader from '../../../components/v2/Loader';
import get from 'lodash/get';
import { DIRECTORY_TYPE_GENERAL, DIRECTORY_TYPE_INHERITED } from 'config/dirs';
import DirectoryTypeField from './DirectoryTypeField';
import { useAvailableTypes } from '../../assets/providers/DirectoryAvailableTypeProvider';
import { createModalFormFooter } from '../../../components/v2/Modal/createModalFormFooter';
import { useTranslation } from 'utils/translation';

type Values = {
  title: string | { [lang: string]: string } | any;
  directoryType: string;
};

type Options = {
  onSuccess?(): void;
};

function useRenameDirectory({ id }: Pick<Dir, 'id'>, options: Options = {}) {
  const modal = useModal();
  const { t } = useTranslation();
  const { availableTypes } = useAvailableTypes();
  const formId = `rename-directory-${id}`;
  const RenameDirectoryModalFooter = React.useMemo(
    () => createModalFormFooter(formId),
    [formId],
  );

  const onSubmit = async ({ title, directoryType }: Values) => {
    await patchUpdateDir({ id, title, directoryType });
  };

  const Component = () => {
    const { data: dir, isLoading, refetch } = useDirQuery(id);
    const { translatableDirectories } = useAppFeatures();
    const { settings } = useAppSettings();

    const onSuccess = () => {
      modal.close(Component);
      options?.onSuccess && options?.onSuccess();
      void refetch();
      void refetchActiveQueries();
    };

    if (isLoading || !dir) {
      return (
        <Section>
          <Loader />
        </Section>
      );
    }

    const defaultTitle = translatableDirectories
      ? Object.fromEntries(
          settings.locales.map((locale: string) => [
            locale,
            get(dir, `translations.${locale}.title`),
          ]),
        )
      : dir.title;

    return (
      <Section>
        <FluentForm<Values>
          formId={formId}
          renderButtons={false}
          onSuccess={onSuccess}
          action={onSubmit}
          defaultValues={{
            title: defaultTitle,
            directoryType: dir.directoryType,
          }}
        >
          <DirectoryTitleField />
          {[DIRECTORY_TYPE_GENERAL, DIRECTORY_TYPE_INHERITED].includes(
            dir.directoryType,
          ) &&
            availableTypes.length > 1 && <DirectoryTypeField />}
        </FluentForm>
      </Section>
    );
  };

  return () => {
    modal.open(Component, {
      title: t('Rename'),
      footer: RenameDirectoryModalFooter,
      size: 'small',
    });
  };
}

export default useRenameDirectory;
